import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  fetchCasaMapaPartnerMarketAreas,
  fetchCasaMapaPartnerMarketBrief,
} from "../../services/casamapaPartnerAgencyApi";
import { useCurrency, useLocale } from "../../intl";
import "../../styles/partnerPortalDashboard.css";
import "../../styles/partnerCasaMapaMarketTools.css";

const COPY = "realEstate.casaMapa.admin.partnerBrief";

export default function PartnerCasaMapaBriefPage() {
  const { path } = usePartnerPortalBase();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const [searchParams] = useSearchParams();
  const [areas, setAreas] = useState(null);
  const [municipality, setMunicipality] = useState(searchParams.get("municipality") || "Medellín");
  const [neighborhood, setNeighborhood] = useState(searchParams.get("neighborhood") || "");
  const [boundaryId, setBoundaryId] = useState(searchParams.get("boundaryId") || "");
  const [listingType, setListingType] = useState(searchParams.get("listingType") || "sale");
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [autoRan, setAutoRan] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCasaMapaPartnerMarketAreas()
      .then((result) => {
        if (!cancelled) setAreas(result);
      })
      .catch(() => {
        if (!cancelled) {
          setAreas({ municipalities: [], neighborhoodsByMunicipality: {}, barriosByMunicipality: {} });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const barrios = areas?.barriosByMunicipality?.[municipality] || [];
  const fallbackNeighborhoods = areas?.neighborhoodsByMunicipality?.[municipality] || [];
  const areaOptions = useMemo(() => {
    if (barrios.length) {
      return barrios.map((row) => ({
        value: row.boundaryId,
        label: `${row.name} (${row.activeListings})`,
        name: row.name,
        boundaryId: row.boundaryId,
      }));
    }
    return fallbackNeighborhoods.map((row) => ({
      value: row.name,
      label: `${row.name} (${row.activeListings})`,
      name: row.name,
      boundaryId: "",
    }));
  }, [barrios, fallbackNeighborhoods]);

  const selectedAreaValue = boundaryId || neighborhood;

  const loadBrief = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaPartnerMarketBrief({
        municipality: municipality || undefined,
        neighborhood: neighborhood || undefined,
        boundaryId: boundaryId || undefined,
        listingType,
        days: 30,
      });
      setBrief(result.brief || null);
    } catch (err) {
      setBrief(null);
      setError(err?.message || t(`${COPY}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [boundaryId, listingType, municipality, neighborhood, t]);

  useEffect(() => {
    if (autoRan) return;
    if (
      !searchParams.get("municipality") &&
      !searchParams.get("neighborhood") &&
      !searchParams.get("boundaryId")
    ) {
      return;
    }
    setAutoRan(true);
    loadBrief();
  }, [autoRan, loadBrief, searchParams]);

  function handleAreaChange(event) {
    const value = event.target.value;
    const option = areaOptions.find((row) => row.value === value);
    if (!option) {
      setBoundaryId("");
      setNeighborhood("");
      return;
    }
    setBoundaryId(option.boundaryId || "");
    setNeighborhood(option.name || "");
  }

  function handleCopyShareLink() {
    const params = new URLSearchParams();
    if (municipality) params.set("municipality", municipality);
    if (neighborhood) params.set("neighborhood", neighborhood);
    if (boundaryId) params.set("boundaryId", boundaryId);
    if (listingType) params.set("listingType", listingType);
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    navigator.clipboard?.writeText(url).then(
      () => {
        setShareCopied(true);
        window.setTimeout(() => setShareCopied(false), 2000);
      },
      () => setShareCopied(false)
    );
  }

  const activity = brief?.activity;
  const pricing = brief?.pricingSignals;
  const titleNeighborhood = brief?.area?.neighborhood || neighborhood;
  const titleMunicipality = brief?.area?.municipality || municipality;

  return (
    <div className="cm-partner-market-tool">
      <header className="partner-dashboard-hero">
        <div>
          <p className="cm-partner-market-tool__eyebrow">{t(`${COPY}.eyebrow`)}</p>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="partner-dashboard-hero__intro">{t(`${COPY}.subtitle`)}</p>
        </div>
        <div className="partner-dashboard-hero__actions">
          <Link className="partner-dashboard-btn" to={path("market")}>
            {t(`${COPY}.back`)}
          </Link>
          <button type="button" className="partner-dashboard-btn" onClick={handleCopyShareLink}>
            {shareCopied ? t(`${COPY}.shareCopied`) : t(`${COPY}.shareLink`)}
          </button>
          <button type="button" className="partner-dashboard-btn" onClick={() => window.print()}>
            {t(`${COPY}.print`)}
          </button>
        </div>
      </header>

      <section className="partner-panel cm-partner-market-tool__controls">
        <label>
          <span>{t(`${COPY}.municipality`)}</span>
          <select
            value={municipality}
            onChange={(e) => {
              setMunicipality(e.target.value);
              setNeighborhood("");
              setBoundaryId("");
            }}
          >
            {(areas?.municipalities || [{ name: "Medellín" }]).map((row) => (
              <option key={row.name || row.slug} value={row.name}>
                {row.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>{t(`${COPY}.neighborhood`)}</span>
          <select value={selectedAreaValue} onChange={handleAreaChange}>
            <option value="">{t(`${COPY}.allNeighborhoods`)}</option>
            {areaOptions.map((row) => (
              <option key={row.value} value={row.value}>
                {row.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>{t(`${COPY}.listingType`)}</span>
          <select value={listingType} onChange={(e) => setListingType(e.target.value)}>
            <option value="sale">{t(`${COPY}.sale`)}</option>
            <option value="rent">{t(`${COPY}.rent`)}</option>
          </select>
        </label>
        <button
          type="button"
          className="partner-dashboard-btn partner-dashboard-btn--primary"
          onClick={loadBrief}
          disabled={loading}
        >
          {t(`${COPY}.generate`)}
        </button>
      </section>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {loading ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}

      {brief && !loading ? (
        <article className="partner-panel cm-partner-brief">
          <p className="cm-partner-brief__brand">CasaMapa</p>
          <h2>
            {titleNeighborhood || titleMunicipality}
            {titleNeighborhood ? ` · ${titleMunicipality}` : ""}
          </h2>
          <p className="muted">
            {t(`${COPY}.window`, { days: brief.windowDays })} · {brief.listingType}
          </p>

          <div className="cm-partner-brief__kpis">
            <div>
              <span>{t(`${COPY}.active`)}</span>
              <strong>{activity?.activeListings ?? 0}</strong>
            </div>
            <div>
              <span>{t(`${COPY}.newListings`)}</span>
              <strong>{activity?.newListings ?? 0}</strong>
            </div>
            <div>
              <span>{t(`${COPY}.medianPrice`)}</span>
              <strong>
                {activity?.medianPriceCop != null ? formatMoney(activity.medianPriceCop) : "—"}
              </strong>
            </div>
            <div>
              <span>{t(`${COPY}.medianDom`)}</span>
              <strong>{activity?.medianDomDays != null ? activity.medianDomDays : "—"}</strong>
            </div>
            <div>
              <span>{t(`${COPY}.views`)}</span>
              <strong>{activity?.listingViews ?? 0}</strong>
            </div>
            <div>
              <span>{t(`${COPY}.inquiries`)}</span>
              <strong>{activity?.inquiries ?? 0}</strong>
            </div>
          </div>

          {pricing?.available ? (
            <p className="cm-partner-brief__pricing">
              {t(`${COPY}.pricingBand`)}: <strong>{pricing.band || pricing.label || "—"}</strong>
              {pricing.relativePositionPercent != null
                ? ` (${pricing.relativePositionPercent > 0 ? "+" : ""}${pricing.relativePositionPercent}% vs median)`
                : ""}
            </p>
          ) : null}

          <h3>{t(`${COPY}.compsTitle`)}</h3>
          {brief.comps?.length ? (
            <ul className="cm-partner-brief__comps">
              {brief.comps.map((listing) => (
                <li key={listing.id}>
                  <strong>{listing.title}</strong>
                  <span>
                    {listing.priceCop != null ? formatMoney(listing.priceCop) : "—"}
                    {listing.areaSqm != null ? ` · ${listing.areaSqm} m²` : ""}
                    {listing.daysOnMarket != null ? ` · ${listing.daysOnMarket}d` : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t(`${COPY}.compsEmpty`)}</p>
          )}
        </article>
      ) : null}
    </div>
  );
}
